import { Quote, QuoteItem, ApiResponse, QuoteItemCreate } from '../types';
import { get, post, put, del, patch } from './api-client';

const BASE_URL = '/quotes';

export const getQuotes = async (search?: string): Promise<ApiResponse<Quote[]>> => {
  const params = new URLSearchParams();
  if (search) params.append('search', search);

  const url = `${BASE_URL}/?${params.toString()}/`;
  const data = await get<Quote[]>(url);
  return { data, success: true };
};

export const getQuoteById = async (quoteId: string): Promise<ApiResponse<Quote>> => {
  const data = await get<Quote>(`${BASE_URL}/${quoteId}/`);
  return { data, success: true };
};

/**
 * Fetches a quote with its items included
 */
export const getQuoteWithItems = async (quoteId: string): Promise<ApiResponse<Quote>> => {
  const data = await get<Quote>(`${BASE_URL}/${quoteId}/with-items`);
  return { data, success: true };
};

export const createQuote = async (
  quote: Omit<Quote, 'id' | 'items'> & {
    status?: Quote['status'];
    items: QuoteItemCreate[];
  }
): Promise<ApiResponse<Quote>> => {
  const data = await post<Quote, typeof quote>(BASE_URL + '/', quote);
  return { data, success: true };
};

export const updateQuote = async (
  quoteId: string,
  quote: Partial<Quote>
): Promise<ApiResponse<Quote>> => {
  const data = await put<Quote, Partial<Quote>>(`${BASE_URL}/${quoteId}/`, quote);
  return { data, success: true };
};

export const deleteQuote = async (quoteId: string): Promise<ApiResponse<void>> => {
  const response = await del<void>(`${BASE_URL}/${quoteId}`);
  return {
    success: true,
    data: undefined,
  };
};

export const submitQuote = async (quoteId: string): Promise<ApiResponse<Quote>> => {
  const data = await put<Quote, Record<string, never>>(`${BASE_URL}/${quoteId}/submit/`, {});
  return { data, success: true };
};

export const approveQuote = async (quoteId: string): Promise<ApiResponse<Quote>> => {
  const data = await put<Quote, Record<string, never>>(`${BASE_URL}/${quoteId}/approve/`, {});
  return { data, success: true };
};

export const rejectQuote = async (quoteId: string, reason: string): Promise<ApiResponse<Quote>> => {
  const data = await put<Quote, { reason: string }>(`${BASE_URL}/${quoteId}/reject/`, { reason });
  return { data, success: true };
};

export const convertQuoteToOrder = async (
  quoteId: string,
  verifyCredit?: boolean
): Promise<ApiResponse<{ order_id: string }>> => {
  try {
    const payload = verifyCredit !== undefined ? { verify_credit: verifyCredit } : {};
    const data = await post<{ order_id: string }, Record<string, any>>(
      `/orders/quotes/${quoteId}/convert`,
      payload
    );
    return { data, success: true };
  } catch (error: any) {
    // Verificamos se o erro está relacionado a falha de crédito
    if (error.response && error.response.status === 400) {
      // Caso: Comprador não analisado/cadastrado na Credix
      const detail = error.response.data?.detail;

      // Se o backend retornou um objeto, extrair a mensagem
      if (detail && typeof detail === 'object') {
        const friendly = detail.message || 'Falha na análise de crédito.';
        return {
          data: null as any,
          success: false,
          message: friendly,
        };
      }
      if (
        typeof detail === 'string' &&
        (detail.toLowerCase().includes('comprador não analisado') ||
          detail.toLowerCase().includes('comprador nao analisado') ||
          detail.toLowerCase().includes('buyer not analyzed'))
      ) {
        return {
          data: null as any,
          success: false,
          message: detail,
        };
      }

      // Caso 1: O backend retorna um objeto com error_type
      if (error.response.data && error.response.data.error_type === 'credit_check_failed') {
        return {
          data: null as any,
          success: false,
          message:
            error.response.data.message ||
            'Cotação enviada para análise de crédito. O pedido não pode ser criado no momento.',
        };
      }

      // Caso 2: O erro contém mensagem relacionada a crédito ou elegibilidade
      const errorDetail = detail || '';
      if (
        typeof errorDetail === 'string' &&
        (errorDetail.includes('eligibility') ||
          errorDetail.includes('credit') ||
          errorDetail.includes('Credix') ||
          errorDetail.includes('buyer'))
      ) {
        // Atualizamos o status da cotação para "Analise de Credito" no frontend
        try {
          await updateQuote(quoteId, { status: 'Analise de Credito' });
        } catch (updateError) {
          console.error('Erro ao atualizar status da cotação:', updateError);
        }

        return {
          data: null as any,
          success: false,
          message:
            'Cotação enviada para análise de crédito. O pedido não pode ser criado no momento.',
        };
      }
    }

    // Quando usamos o api-client, os erros são normalizados para Error com .message
    const errMessage: string = error && error.message ? String(error.message) : '';
    if (errMessage) {
      const normalized = errMessage
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
      if (
        normalized.includes('comprador nao analisado') ||
        normalized.includes('buyer not analyzed') ||
        normalized.includes('comprador nao cadastrado')
      ) {
        return {
          data: null as any,
          success: false,
          message: errMessage,
        };
      }
    }

    // Para outros erros, apenas retornamos o erro
    return {
      data: null as any,
      success: false,
      message:
        errMessage ||
        (typeof error?.response?.data?.detail === 'string'
          ? error?.response?.data?.detail
          : error?.response?.data?.detail?.message) ||
        error?.response?.data?.message ||
        'Erro ao converter cotação em pedido.',
    };
  }
};

export const uploadQuoteConversion = async (
  quoteId: string,
  formData: FormData
): Promise<
  ApiResponse<{
    quote_id: string;
    status: string;
    file_uploaded: boolean;
    s3_key: string;
    s3_url: string;
    file_path: string;
    message: string;
  }>
> => {
  try {
    const data = await post<
      {
        quote_id: string;
        status: string;
        file_uploaded: boolean;
        s3_key: string;
        s3_url: string;
        file_path: string;
        message: string;
      },
      FormData
    >(`${BASE_URL}/${quoteId}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return { data, success: true };
  } catch (error: any) {
    return {
      data: null as any,
      success: false,
      message: error.response?.data?.detail || error.response?.data?.message || 'Erro no upload.',
    };
  }
};

// Quote Item endpoints
export const getQuoteItems = async (quoteId: string): Promise<ApiResponse<QuoteItem[]>> => {
  const data = await get<QuoteItem[]>(`${BASE_URL}/${quoteId}/items/`);
  return { data, success: true };
};

export const addQuoteItem = async (
  quoteId: string,
  item: Omit<QuoteItem, 'id' | 'quote_id' | 'product'>
): Promise<ApiResponse<QuoteItem>> => {
  const data = await post<QuoteItem, Omit<QuoteItem, 'id' | 'quote_id' | 'product'>>(
    `${BASE_URL}/${quoteId}/items`,
    item
  );
  return { data, success: true };
};

export const updateQuoteItem = async (
  quoteId: string,
  itemId: string,
  item: Partial<QuoteItem>
): Promise<ApiResponse<QuoteItem>> => {
  const data = await patch<QuoteItem, Partial<QuoteItem>>(
    `${BASE_URL}/${quoteId}/items/${itemId}/`,
    item
  );
  return { data, success: true };
};

export const removeQuoteItem = async (
  quoteId: string,
  itemId: string
): Promise<ApiResponse<void>> => {
  await del<void>(`${BASE_URL}/${quoteId}/items/${itemId}/`);
  return { data: undefined, success: true };
};
